import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

type CookieConsent = 'accepted' | 'rejected' | null;

export default function CookieBanner() {
  const [consent, setConsent] = useState<CookieConsent>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [thirdPartyEnabled, setThirdPartyEnabled] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('lios_cookie_consent');
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    } else {
      setConsent(stored as CookieConsent);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('lios_cookie_consent', 'accepted');
    localStorage.setItem('lios_cookie_analytics', 'true');
    localStorage.setItem('lios_cookie_third_party', 'true');
    setConsent('accepted');
    setVisible(false);
  };

  const handleRejectAll = () => {
    localStorage.setItem('lios_cookie_consent', 'rejected');
    localStorage.setItem('lios_cookie_analytics', 'false');
    localStorage.setItem('lios_cookie_third_party', 'false');
    setConsent('rejected');
    setVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('lios_cookie_consent', 'accepted');
    localStorage.setItem('lios_cookie_analytics', String(analyticsEnabled));
    localStorage.setItem('lios_cookie_third_party', String(thirdPartyEnabled));
    setConsent('accepted');
    setVisible(false);
  };

  if (!visible || consent !== null) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6">
      <div
        className={`max-w-[960px] mx-auto bg-white rounded-2xl border border-gray-200 overflow-hidden transition-all duration-500 ${
          visible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
        style={{ boxShadow: '0 -4px 40px rgba(0,0,0,0.10)' }}
      >
        {/* Main Banner */}
        {!showDetails && (
          <div className="p-6 md:p-8">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
              {/* Icon + Text */}
              <div className="flex items-start gap-4 flex-1">
                <div className="w-10 h-10 flex items-center justify-center bg-[#1A4D4D]/10 rounded-xl shrink-0 mt-0.5">
                  <i className="ri-shield-check-line text-[#1A4D4D] text-xl"></i>
                </div>
                <div>
                  <h2 className="text-base font-bold text-[#2C2C2C] mb-1">
                    Questo sito utilizza i cookie
                  </h2>
                  <p className="text-sm text-gray-500 font-light leading-relaxed">
                    Utilizziamo cookie tecnici necessari al funzionamento del sito e, previo consenso, cookie analitici per migliorare la tua esperienza. Puoi accettare tutti i cookie, rifiutarli o personalizzare le tue preferenze.{' '}
                    <Link
                      to="/privacy-policy"
                      className="text-[#1A4D4D] font-medium hover:underline cursor-pointer"
                    >
                      Leggi la Privacy Policy
                    </Link>
                  </p>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0 w-full lg:w-auto">
                <button
                  onClick={() => setShowDetails(true)}
                  className="px-5 py-2.5 text-sm font-medium text-[#2C2C2C] border border-gray-300 rounded-full hover:border-[#1A4D4D] hover:text-[#1A4D4D] transition-all whitespace-nowrap cursor-pointer"
                >
                  Personalizza
                </button>
                <button
                  onClick={handleRejectAll}
                  className="px-5 py-2.5 text-sm font-medium text-gray-500 border border-gray-200 rounded-full hover:border-gray-400 transition-all whitespace-nowrap cursor-pointer"
                >
                  Rifiuta tutti
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-2.5 text-sm font-medium bg-[#1A4D4D] text-white rounded-full hover:bg-[#2D5F5F] transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  Accetta tutti
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Detailed Preferences Panel */}
        {showDetails && (
          <div className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={() => setShowDetails(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-[#1A4D4D] transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-lg"></i>
              </button>
              <h2 className="text-base font-bold text-[#2C2C2C]">Gestisci le preferenze cookie</h2>
            </div>

            <div className="space-y-4 mb-6">
              {/* Cookie tecnici - sempre attivi */}
              <div className="flex items-start justify-between gap-4 p-4 bg-[#F8F6F3] rounded-xl">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-semibold text-[#2C2C2C]">Cookie tecnici</p>
                    <span className="text-xs bg-[#1A4D4D]/10 text-[#1A4D4D] px-2 py-0.5 rounded-full font-medium">Sempre attivi</span>
                  </div>
                  <p className="text-xs text-gray-500 font-light leading-relaxed">
                    Necessari per il corretto funzionamento del sito. Non possono essere disabilitati in quanto essenziali per la navigazione.
                  </p>
                </div>
                <div className="shrink-0 mt-1">
                  <div className="w-11 h-6 bg-[#1A4D4D] rounded-full flex items-center justify-end px-1 cursor-not-allowed opacity-60">
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Cookie analitici */}
              <div className="flex items-start justify-between gap-4 p-4 bg-[#F8F6F3] rounded-xl">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#2C2C2C] mb-1">Cookie analitici</p>
                  <p className="text-xs text-gray-500 font-light leading-relaxed">
                    Ci aiutano a capire come gli utenti interagiscono con il sito, raccogliendo informazioni in forma anonima per migliorare i contenuti.
                  </p>
                </div>
                <button
                  onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
                  className="shrink-0 mt-1 cursor-pointer"
                  aria-label="Toggle cookie analitici"
                >
                  <div className={`w-11 h-6 rounded-full flex items-center px-1 transition-all duration-300 ${analyticsEnabled ? 'bg-[#1A4D4D] justify-end' : 'bg-gray-300 justify-start'}`}>
                    <div className="w-4 h-4 bg-white rounded-full transition-all duration-300"></div>
                  </div>
                </button>
              </div>

              {/* Cookie terze parti */}
              <div className="flex items-start justify-between gap-4 p-4 bg-[#F8F6F3] rounded-xl">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#2C2C2C] mb-1">Cookie di terze parti</p>
                  <p className="text-xs text-gray-500 font-light leading-relaxed">
                    Utilizzati da servizi esterni come Google Maps integrati nel sito. Possono raccogliere dati per le proprie finalità.
                  </p>
                </div>
                <button
                  onClick={() => setThirdPartyEnabled(!thirdPartyEnabled)}
                  className="shrink-0 mt-1 cursor-pointer"
                  aria-label="Toggle cookie terze parti"
                >
                  <div className={`w-11 h-6 rounded-full flex items-center px-1 transition-all duration-300 ${thirdPartyEnabled ? 'bg-[#1A4D4D] justify-end' : 'bg-gray-300 justify-start'}`}>
                    <div className="w-4 h-4 bg-white rounded-full transition-all duration-300"></div>
                  </div>
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
              <button
                onClick={handleRejectAll}
                className="px-5 py-2.5 text-sm font-medium text-gray-500 border border-gray-200 rounded-full hover:border-gray-400 transition-all whitespace-nowrap cursor-pointer"
              >
                Rifiuta tutti
              </button>
              <button
                onClick={handleSavePreferences}
                className="px-6 py-2.5 text-sm font-medium bg-[#1A4D4D] text-white rounded-full hover:bg-[#2D5F5F] transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Salva preferenze
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-6 py-2.5 text-sm font-medium bg-[#FF6B35] text-white rounded-full hover:bg-[#e55a25] transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Accetta tutti
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
